import { Ban } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsDnc } from "@/hooks/useDoNotContact";
import { cn } from "@/lib/utils";

interface DncBadgeProps {
  accountId?: string | null;
  contactId?: string | null;
  size?: "sm" | "md";
  className?: string;
  /** When true, render an icon-only badge (for tight kanban cards). */
  iconOnly?: boolean;
}

export function DncBadge({
  accountId,
  contactId,
  size = "sm",
  className,
  iconOnly = false,
}: DncBadgeProps) {
  const { blocked, entry } = useIsDnc({ accountId, contactId });
  if (!blocked) return null;

  const tooltipText = entry
    ? [
        `DNC: ${entry.scope}`,
        entry.reason && entry.reason !== "unspecified" ? `Reason: ${entry.reason}` : null,
        entry.notes ? `Notes: ${entry.notes}` : null,
        `Added by: ${entry.added_by}`,
        new Date(entry.added_at).toLocaleDateString(),
      ]
        .filter(Boolean)
        .join(" · ")
    : "Do Not Contact";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={cn(
              "border-red-300 bg-red-50 text-red-800 hover:bg-red-100",
              size === "sm" ? "h-5 px-1.5 text-[10px]" : "h-6 px-2 text-xs",
              className,
            )}
          >
            <Ban className={cn(size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5", iconOnly ? "" : "mr-1")} />
            {!iconOnly && "DNC"}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
